import { OrderWastageRes, PerformanceName } from 'services/api/dashboard';
import { DashboardFilterType } from '../Filter/useFilter';

type SpiltKeyByPlatform<P extends 'lazada' | 'shopee', S extends Record<string, any>> = {
  [K in keyof S]: K extends `${P}${string}` ? K : never;
}[keyof S];
type ShopeePackageName = SpiltKeyByPlatform<'shopee', OrderWastageRes>;
type LazadaPackageName = SpiltKeyByPlatform<'lazada', OrderWastageRes>;

type ShopeeStatGroupsName = SpiltKeyByPlatform<
  'shopee',
  Util.RequiredDeep<Dashboard.OrderWastageInner>
>;
type LazadaStatGroupsName = SpiltKeyByPlatform<
  'lazada',
  Util.RequiredDeep<Dashboard.OrderWastageInner>
>;

export type LazadaStatsName = Util.AllKeysOf<
  Util.RequiredDeep<Dashboard.OrderWastageInner>[LazadaStatGroupsName]
>;
export type ShopeeStatsName = Util.AllKeysOf<
  Util.RequiredDeep<Dashboard.OrderWastageInner>[ShopeeStatGroupsName]
>;

function group(
  input: Partial<OrderWastageRes>,
  packageName: LazadaPackageName,
  key: keyof OrderWastageRes[LazadaPackageName],
): Dashboard.OrderWastageStage;
function group(
  input: Partial<OrderWastageRes>,
  packageName: ShopeePackageName,
  key: keyof OrderWastageRes[ShopeePackageName],
): Dashboard.OrderWastageStage;
function group(
  input: Partial<OrderWastageRes>,
  _packageName: LazadaPackageName | ShopeePackageName,
  _key: keyof OrderWastageRes[ShopeePackageName] | keyof OrderWastageRes[LazadaPackageName],
) {
  const isShopee = _packageName.startsWith('shopee');
  if (isShopee) {
    const packageName = _packageName as ShopeePackageName;
    const key = _key as keyof OrderWastageRes[ShopeePackageName];
    return {
      订单损耗量: input[packageName]?.[key]?.orderLossCount,
      订单损耗率: input[packageName]?.[key]?.orderLossRate,
      领先比率: input[packageName]?.[key]?.overallBeatRatio,
      订单总量: input[packageName]?.[key]?.allOrderCount,
      订单留存率: input[packageName]?.[key]?.orderRetentionRate,
      订单留存量: input[packageName]?.[key]?.orderRetention,
    };
  }
  const packageName = _packageName as LazadaPackageName;
  const key = _key as keyof OrderWastageRes[LazadaPackageName];
  return {
    订单损耗量: input[packageName]?.[key]?.orderLossCount,
    订单损耗率: input[packageName]?.[key]?.orderLossRate,
    领先比率: input[packageName]?.[key]?.overallBeatRatio,
    订单总量: input[packageName]?.[key]?.allOrderCount,
    订单留存率: input[packageName]?.[key]?.orderRetentionRate,
    订单留存量: input[packageName]?.[key]?.orderRetention,
  };
}

interface LossStats {
  totalLossCount?: number;
  totalLossRate?: number;
}

function calcTotalLossStats(
  input: Partial<OrderWastageRes>,
  packageName: LazadaPackageName | ShopeePackageName,
): LossStats {
  const stats = input[packageName];
  if (!stats) {
    return {};
  }
  let totalOrderCount = 0;
  const totalLossCount = Object.keys(stats).reduce((res, statKey) => {
    // 每个流程stats里都包含订单总量的值，因此订单总量不需要sum，只取最大的有效值就可以了。
    totalOrderCount = Math.max(stats[statKey].allOrderCount || 0, totalOrderCount);
    // 每个流程的stats 只含有本流程的订单loss，因此总loss count需要sum
    return res + (stats[statKey].orderLossCount || 0);
  }, 0);
  const totalLossRate = totalLossCount / totalOrderCount;
  return { totalLossCount, totalLossRate };
}
export interface OrderWastageCoveredData {
  charts: Partial<Dashboard.OrderWastageInner>;
  lossStats: Record<keyof Dashboard.OrderWastageInner, LossStats>;
}
export default function convertData(input: Partial<OrderWastageRes>): OrderWastageCoveredData {
  const charts = {
    lazadaCOD订单损耗情况: {
      下单: group(input, 'lazadaOrderLossCod', 'unpaidToPaidCancelCod'),
      订单确认: group(input, 'lazadaOrderLossCod', 'paidToPackedCancelCod'),
      打包: group(input, 'lazadaOrderLossCod', 'packedToReadyToShipCancelCod'),
      卖家点击发货: group(input, 'lazadaOrderLossCod', 'readyToShipToShippedCancelCod'),
      揽收: group(input, 'lazadaOrderLossCod', 'shippedToCompletedCancelCod'),
      买家收货: group(input, 'lazadaOrderLossCod', 'afterCompletedCancelCod'),
      订单结束: group(input, 'lazadaOrderLossCod', 'noneStatusCancelCod'),
    },
    lazada非COD订单损耗情况: {
      下单: group(input, 'lazadaOrderLossNoCod', 'unpaidToPaidCancelCod'),
      付款: group(input, 'lazadaOrderLossNoCod', 'paidToPackedCancelCod'),
      打包: group(input, 'lazadaOrderLossNoCod', 'packedToReadyToShipCancelCod'),
      卖家点击发货: group(input, 'lazadaOrderLossNoCod', 'readyToShipToShippedCancelCod'),
      揽收: group(input, 'lazadaOrderLossNoCod', 'shippedToCompletedCancelCod'),
      买家收货: group(input, 'lazadaOrderLossNoCod', 'afterCompletedCancelCod'),
      订单结束: group(input, 'lazadaOrderLossNoCod', 'noneStatusCancelCod'),
    },
    shopee非COD订单损耗情况: {
      下单: group(input, 'shopeeOrderLossNoCod', 'unpaidToPaidCancelCod'),
      付款: group(input, 'shopeeOrderLossNoCod', 'paidToReadyToShipCancelCod'),
      卖家点击发货: group(input, 'shopeeOrderLossNoCod', 'readyToShipToShippedCancelCod'),
      揽收: group(input, 'shopeeOrderLossNoCod', 'shippedToCompletedCancelCod'),
      买家收货: group(input, 'shopeeOrderLossNoCod', 'afterCompletedCancelCod'),
      订单结束: group(input, 'shopeeOrderLossNoCod', 'noneStatusCancelCod'),
    },
    shopeeCOD订单损耗情况: {
      下单: group(input, 'shopeeOrderLossCod', 'unpaidToPaidCancelCod'),
      订单确认: group(input, 'shopeeOrderLossCod', 'paidToReadyToShipCancelCod'),
      卖家点击发货: group(input, 'shopeeOrderLossCod', 'readyToShipToShippedCancelCod'),
      揽收: group(input, 'shopeeOrderLossCod', 'shippedToCompletedCancelCod'),
      买家收货: group(input, 'shopeeOrderLossCod', 'afterCompletedCancelCod'),
      订单结束: group(input, 'shopeeOrderLossCod', 'noneStatusCancelCod'),
    },
  };
  const lossStats = {
    lazadaCOD订单损耗情况: calcTotalLossStats(input, 'lazadaOrderLossCod'),
    lazada非COD订单损耗情况: calcTotalLossStats(input, 'lazadaOrderLossNoCod'),
    shopee非COD订单损耗情况: calcTotalLossStats(input, 'shopeeOrderLossNoCod'),
    shopeeCOD订单损耗情况: calcTotalLossStats(input, 'shopeeOrderLossCod'),
  };
  return { charts, lossStats };
}

export type StatsPerformanceName = Exclude<
  PerformanceName,
  // 订单概况页面
  | 'REORDER_RATIO' // 复购率
  // 销售转化页面
  | 'PRE_SALE_CONSULT_CONVERSION_RATE' // 售前咨询转化率
  | 'OVERALL_CONSULT_CONVERSION_RATE' // 整体咨询转化率
  | 'ORDER_REMINDER_CONVERSION_RATE' // 催单后下单买家比例
  | 'PAYMENT_REMINDER_CONVERSION_RATE' // 催付款后付款比例

  // 评价数据页面
  | 'BAD_COMMENT_RATE' // 买家评价：中差评率
  | 'PRAISE_REMINDER_CONVERSION_RATE' // 自动催好评：自动催好评成功率
  | 'FIX_COMMENT_REMINDER_CONVERSION_RATE' // 中差评挽回：中差评挽回率
>;

export type StatsName = Exclude<LazadaStatsName | ShopeeStatsName, '订单结束'>;
type CODStatsName = Exclude<StatsName, '付款'>;
type NotCODeStatsName = Exclude<StatsName, '订单确认'>;
export const statsNameToPerformanceName = (
  _statName: Exclude<LazadaStatsName | ShopeeStatsName, '订单结束'>,
  isCOD = false,
  platform: DashboardFilterType['platform'],
): StatsPerformanceName => {
  if (isCOD) {
    const statName = _statName as CODStatsName;
    // COD订单损耗
    const stats2PerfMap: Record<CODStatsName, StatsPerformanceName> = {
      下单: 'COD_ORDER_TO_CONFIRMED_LOSS_RATE',
      订单确认:
        platform === 'SHOPEE'
          ? 'COD_CONFIRMED_TO_READY_TO_SHIP_LOSS_RATE'
          : 'COD_CONFIRMED_TO_PACKED_LOSS_RATE',
      卖家点击发货: 'COD_READY_TO_SHIP_TO_SHIPPED_LOSS_RATE',
      揽收: 'COD_SHIPPED_TO_RECEIVED_LOSS_RATE',
      买家收货: 'COD_RECEIVED_TO_CLOSED_LOSS_RATE',
      打包: 'COD_PACKED_TO_READY_TO_SHIP_LOSS_RATE',
    };
    return stats2PerfMap[statName];
  }
  const statName = _statName as NotCODeStatsName;
  // 非COD订单损耗
  const notCodStats2PerfMap: Record<NotCODeStatsName, StatsPerformanceName> = {
    下单: 'NOT_COD_ORDER_TO_PAYMENT_LOSS_RATE',
    付款:
      platform === 'SHOPEE'
        ? 'NOT_COD_PAYMENT_TO_READY_TO_SHIP_LOSS_RATE'
        : 'NOT_COD_PAYMENT_TO_PACKED_LOSS_RATE',
    卖家点击发货: 'NOT_COD_READY_TO_SHIP_TO_SHIPPED_LOSS_RATE',
    揽收: 'NOT_COD_SHIPPED_TO_RECEIVED_LOSS_RATE',
    买家收货: 'NOT_COD_RECEIVED_TO_CLOSED_LOSS_RATE',
    打包: 'NOT_COD_PACKED_TO_READY_TO_SHIP_LOSS_RATE',
  };

  return notCodStats2PerfMap[statName];
};
